import { createContext, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useListPartners } from "../pages/users/layout/resuableEffect";
import { debugPartnerConfig, setDevelopmentPartner } from "../hooks/remote/apiClient";

const PartnerContext = createContext();

const isDevelopment = () => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || 
         hostname === '127.0.0.1' || 
         hostname.startsWith('192.168.') ||
         hostname.startsWith('10.') ||
         hostname.endsWith('.local');
};

const detectPartnerSubdomain = () => {
  const urlParams = new URLSearchParams(window.location.search);

  const partnerParam = urlParams.get('partner') || urlParams.get('subdomain');
  if (partnerParam) {
    return partnerParam;
  }

  const hostname = window.location.hostname;
  
  if (hostname.includes('.cloudvantage.com.ng') && 
      hostname.includes('.staging') && 
      hostname !== 'cloudvantage.com.ng' && 
      hostname !== 'staging.cloudvantage.com.ng'
    ) {

    return hostname.split('.')[0];
  }

  if (isDevelopment()) {
    const savedPartner = localStorage.getItem('dev_partner_subdomain');
    if (savedPartner) {
      return savedPartner;
    }
  }

  return null;
};

const detectAdminMode = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const adminParam = urlParams.get('admin');
  return adminParam === 'true';
};

export const PartnerProvider = ({ children }) => {

  const [partnerInfo, setPartnerInfo] = useState(null);
  const [isWhiteLabel, setIsWhiteLabel] = useState(false);
  const [isPartnerAdmin, setIsPartnerAdmin] = useState(false);
  const [currentSubdomain, setCurrentSubdomain] = useState(null);

  const [isDevMode, setIsDevMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAdminMode, setIsAdminMode] = useState(false);

  const userSessionData = useSelector((state) => state.user.userSessionData);
  const dispatch = useDispatch();
  
  const needsPartnerData = Boolean(
    detectPartnerSubdomain() ||  
    detectAdminMode()            
  );
  
  const partners = useListPartners(needsPartnerData);


  const detectPartnerDomain = () => {
    setLoading(true);
    
    const hostname = window.location.hostname;
    const isDev = isDevelopment();
    setIsDevMode(isDev);

    const subdomain = detectPartnerSubdomain();
    const adminMode = detectAdminMode();
    
    setCurrentSubdomain(subdomain);
    setIsAdminMode(adminMode);

    // Reset to default state
    setIsWhiteLabel(false);
    setIsPartnerAdmin(false);
    setPartnerInfo(null);

    if (!subdomain && !adminMode) {
      setLoading(false);
      return; 
    }

    if (subdomain || adminMode) {

      if (adminMode) {
        setIsPartnerAdmin(true);
        setIsWhiteLabel(true);
        
        if (partners && partners.length > 0) {
          const matchingPartner = partners.find(partner => 
            partner.partner_link && partner.partner_link.includes(subdomain)
          );

          if (matchingPartner) {
            console.log("matchingPartner", matchingPartner);
            setPartnerInfo(matchingPartner);
          } else {
            console.warn(`Partner not found for subdomain: ${subdomain}`);
          }
        }
        
        setLoading(false);
        return;
      }

      // Handle admin subdomain pattern
      if (subdomain && subdomain.startsWith('admin-') && !isDev) {
        const partnerSubdomain = subdomain.replace('admin-', '');
        setIsPartnerAdmin(true);
        setCurrentSubdomain(partnerSubdomain);
        setLoading(false);
        return;
      }

      if (subdomain) {
        setIsWhiteLabel(true);

        if (partners && partners.length > 0) {
          const matchingPartner = partners.find(partner => 
            partner.partner_link && partner.partner_link.includes(subdomain)
          );

          console.log("matchingPartner for regular mode", matchingPartner);
          
          if (matchingPartner) {
            setPartnerInfo(matchingPartner);
          } else {
            console.warn(`Partner not found for subdomain: ${subdomain}`);
            if (!isDev) {
              setIsWhiteLabel(false);
            }
          }
        } else {
          console.warn(`No partners available for subdomain: ${subdomain}`);
          if (!isDev) {
            setIsWhiteLabel(false);
          }
        }
      }
    }
    
    setLoading(false);
  };

  const setDevPartner = (subdomain) => {
    if (!isDevelopment()) {
      console.warn('setDevPartner only works in development mode');
      return;
    }

    setDevelopmentPartner(subdomain);
    detectPartnerDomain();
  };

  const clearDevPartner = () => {
    if (!isDevelopment()) {
      console.warn('clearDevPartner only works in development mode');
      return;
    }

    setDevelopmentPartner(null);
    detectPartnerDomain();
  };

  const switchToAdminMode = () => {
    const url = new URL(window.location);
    url.searchParams.set('admin', 'true');
    
    const partnerName = userSessionData?.partner?.name;
    if (partnerName && !url.searchParams.get('partner')) {
      url.searchParams.set('partner', partnerName);
    }
    
    window.location.href = url.toString();
  };

  const switchToCustomerView = () => {
    const partnerName = userSessionData?.partner?.name;

    if(isDevelopment()) {
      if (partnerName) {
        window.location.href = `${window.location.origin}${window.location.pathname}?partner=${partnerName}`;
      }
    } else {
      if (partnerName) {
        window.location.href = `https://${partnerName}.cloudvantage.com.ng`;
      } else {
        // Fallback to removing admin param
        const url = new URL(window.location);
        url.searchParams.delete('admin');
        window.location.href = url.toString();
      }
    }
  };

  const showDebugInfo = () => {
    const config = debugPartnerConfig();
    console.log('Partner Context Debug:', {
      currentSubdomain,
      isWhiteLabel,
      isPartnerAdmin,
      isDevMode,
      isAdminMode,
      partnerInfo: partnerInfo ? partnerInfo.name : null,
      userPartnerData: userSessionData?.partner?.name || null,
      needsPartnerData,
      partnersCount: partners ? partners.length : 0,
      apiConfig: config
    });
    return config;
  };

  useEffect(() => {
    const handleUrlChange = () => {
      detectPartnerDomain();
    };

    window.addEventListener('popstate', handleUrlChange);

    detectPartnerDomain();

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [dispatch, partners]);

  useEffect(() => {
    if (!isDevelopment()) return;

    const handleStorageChange = (e) => {
      if (e.key === 'dev_partner_subdomain') {
        detectPartnerDomain();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const contextValue = {
    partnerInfo,
    isWhiteLabel,
    isPartnerAdmin,
    currentSubdomain,
    loading,
    isDevMode,
    isAdminMode, 
    setDevPartner,
    clearDevPartner,
    switchToAdminMode, 
    switchToCustomerView, 
    showDebugInfo,
    setPartnerInfo,
    refreshPartnerInfo: detectPartnerDomain,
  };

  return (
    <PartnerContext.Provider value={contextValue}>
      {children}
    </PartnerContext.Provider>
  );
};

export const usePartner = () => {
  const context = useContext(PartnerContext);
  if (!context) {
    throw new Error('usePartner must be used within a PartnerProvider');
  }
  return context;
};